<template>
  <div class="reporting__scroll">
    <h3><span class="title" v-html="$t('reporting.scroll.title')"></span></h3>
    <p class="description" v-html="$t('reporting.scroll.desc')"></p>

    <ui-button v-if="hasData"
      class="reporting__export"
      :class="currentLang"
      :size="'small'"
      @click="onExcelExport"
      primary3>
      <i class="icon icon-excel-size16"></i> {{ $t('reporting.export') }}
    </ui-button>

    <!-- no data -->
    <div v-if="!hasData" class="no-data">
      <p v-html="$t('reporting.scroll.noScrollData')"></p>
    </div>
    <div v-else>
      <!-- scroll analysis 1 fold -->
      <div class="reporting__scroll__fold content-box">
        <div>
          <div class="reporting__scroll__fold-title">
            <span class="title" v-html="$t('reporting.scroll.fold.title')"></span>
            <p class="description" v-html="$t('reporting.scroll.fold.desc')"></p>
          </div>
          <div class="reporting__scroll__fold-average">
            <span class="value">
              {{ $t('reporting.scroll.scrolledHeights.legend.averageFold.title') }}
              {{ Math.round(avgFold) }}px
            </span>
            <p class="description" v-html="$t('reporting.scroll.fold.average')"></p>
          </div>
          <div class="reporting__scroll__fold-chart-legend">
            <ul>
              <li>
                <span class="legend" v-html="$t('reporting.scroll.fold.legend.above.title')"></span>
                <ui-icon-tooltip><span v-html="$t('reporting.scroll.fold.legend.above.desc')"></span></ui-icon-tooltip>
              </li>
              <li>
                <ui-icon-tooltip arrowRight><span v-html="$t('reporting.scroll.fold.legend.below.desc')"></span></ui-icon-tooltip>
                <span class="legend" v-html="$t('reporting.scroll.fold.legend.below.title')"></span>
              </li>
            </ul>
            <p class="chart-name">{{ $t('reporting.scroll.fold.aboveVersusBelow') }}</p>
          </div>
          <div class="reporting__scroll__fold-chart">
            <div v-for="item in foldItems" :key="item.label"
              class="chart_wrap clearfix">
              <div class="left">
                <app-chart chartType="bar"
                  :chartOptions="getChartOptions(item, true)"
                  :height="80">
                </app-chart>
              </div>
              <div class="label">{{ item.label }}</div>
              <div class="right">
                <app-chart chartType="bar"
                  :chartOptions="getChartOptions(item, false)"
                  :height="80">
                </app-chart>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- scroll analysis 2 scroll Height -->
      <div class="reporting__scroll__scrolled-heights content-box">
        <div class="reporting__scroll__scrolled-heights-title">
          <span class="title" v-html="$t('reporting.scroll.scrolledHeights.title')"></span>
          <p class="description" v-html="$t('reporting.scroll.scrolledHeights.desc')"></p>
        </div>
        <div class="reporting__scroll__scrolled-heights-terms">
          <ul>
            <li class="terms">
              <i></i>
              <span v-html="$t('reporting.scroll.scrolledHeights.legend.scrolledArea.title')"></span>
              <p v-html="$t('reporting.scroll.scrolledHeights.legend.scrolledArea.desc')"></p>
            </li>
            <li class="terms">
              <i class="icon icon--average-fold"></i>
              <span v-html="$t('reporting.scroll.scrolledHeights.legend.averageFold.title')"></span>
              <p v-html="$t('reporting.scroll.scrolledHeights.legend.averageFold.desc')"></p>
            </li>
          </ul>
        </div>
        <div class="reporting__scroll__scolled-heights-chart">
          <div class="scroll_pv_rate_chart_container" :style="scrollPvRateChartContainerStyle">
            <div class="scroll_pv_rate_chart_image" ref="imageContainer"
              :style="scrollPvRateChartImageStyle"></div>
            <app-chart chartType="bar" v-if="chartImageHeight"
              :chartOptions="scrollPvRateChartOptions"
              :height="chartHeight">
            </app-chart>
            <div class="scroll_avg_fold" :style="scrollAvgFoldStyle"></div>
          </div>
        </div>
      </div>
      <slot name="footer"/>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { getTooltipHtml } from '@/libs/chart-helper'
import Excel from '@/libs/excel-export'
import AppChart from '@/components/chart'
import UiIconTooltip from '@/components/ui/UiIconTooltip'
import UiButton from '@/components/ui/UiButton'
import { NEW_MOBILE_HEATMAP_STARTDATE } from '@/libs/constants'

const PHONE_IMAGE_WIDTH = 400
const ATTENTION_GRID = {
  left: 70,
  top: 20,
  right: 20,
  bottom: 0,
  containLabel: false
}

export default {
  name: 'ReportScroll',
  components: {
    AppChart,
    UiIconTooltip,
    UiButton
  },
  props: [
    'isLoading',
    'reportName',
    'reportData',
    'infoData',
    'referer',
    'metric',
    'activeSelector'
  ],
  data () {
    return {
      chartImageWidth: 0
    }
  },
  computed: {
    ...mapState([
      'resizeTimestamp',
      'applyData',
      'reportPeriod'
    ]),
    currentLang () {
      return this.$i18n.locale
    },
    isDesktopDevice () {
      return this.reportData && this.reportData.device === 'desktop'
    },
    isPhoneDevice () {
      return this.reportData && this.reportData.device === 'phone'
    },
    imageData () {
      return this.reportData && this.reportData.image
    },
    imageRatio () {
      return this.imageData.width / this.imageData.height
    },
    chartImageHeight () {
      return this.chartImageWidth / this.imageRatio
    },
    chartHeight () {
      return this.chartImageHeight + ATTENTION_GRID.top
    },
    scrollPvRateChartContainerStyle () {
      if (!this.isPhoneDevice) {
        return null
      }

      const maxWidth = `${PHONE_IMAGE_WIDTH + ATTENTION_GRID.left + ATTENTION_GRID.right}px`
      return {
        maxWidth,
        margin: '0 auto'
      }
    },
    scrollPvRateChartImageStyle () {
      return {
        left: ATTENTION_GRID.left + 'px',
        right: ATTENTION_GRID.right + 'px',
        top: ATTENTION_GRID.top + 'px',
        height: this.chartImageHeight + 'px',
        backgroundImage: `url(${this.imageData.url})`
      }
    },
    scrollAvgFoldStyle () {
      const chartAvgFold = this.avgFold * (this.chartImageHeight / this.imageData.height)
      return {
        left: ATTENTION_GRID.left + 'px',
        right: ATTENTION_GRID.right + 'px',
        top: `${chartAvgFold + ATTENTION_GRID.top}px`
      }
    },
    rawData () {
      if (this.reportData && this.reportData.data) {
        return this.reportData.data.scroll
      }
      return null
    },
    hasData () {
      return this.attentionItems && this.attentionItems.length
    },
    avgFold () {
      if (this.rawData) {
        return this.rawData.fold.aboveSize
      }
      return null
    },
    attentionItems () {
      let items = null

      if (this.rawData) {
        items = this.rawData.attentionItems
      }

      return items || null
    },
    scrollPvRateChartOptions () {
      if (!this.attentionItems) {
        return null
      }

      const chartData = this.rawData.scrollPvRateChartData
      const thousandFilter = this.$options.filters.thousand

      return {
        grid: ATTENTION_GRID,
        tooltip: {
          trigger: 'item',
          formatter (item) {
            const title = thousandFilter(item.value[1]) + 'px'
            const content = [
              ['PV :', `${item.value[0]}%`]
            ]
            return getTooltipHtml(content, title)
          }
        },
        xAxis: {
          type: 'value',
          boundaryGap: true,
          position: 'top',
          axisLine: {
            show: true
          },
          axisTick: {
            show: true
          },
          axisLabel: {
            formatter: '{value}%'
          }
        },
        yAxis: {
          type: 'category',
          inverse: true,
          axisLine: {
            show: true
          },
          axisTick: {
            show: true
          },
          axisLabel: {
            formatter: (value) => {
              return thousandFilter(value) + 'px'
            }
          }
        },
        series: [
          {
            type: 'bar',
            data: chartData,
            barWidth: Math.floor(this.chartImageHeight / chartData.length),
            itemStyle: {
              opacity: 0.5
            }
          }
        ]
      }
    },
    foldItems () {
      if (!this.rawData) {
        return
      }
      const foldData = this.rawData.fold

      const items = [
        {
          key: 'height',
          label: 'Height(px)',
          max: Math.round(Math.max(foldData.aboveSize, foldData.belowSize)),
          aboveValue: Math.round(foldData.aboveSize),
          belowValue: Math.round(foldData.belowSize)
        },
        {
          key: 'click',
          label: 'Tap(cnt)',
          max: Math.max(foldData.aboveClick, foldData.belowClick),
          aboveValue: foldData.aboveClick,
          belowValue: foldData.belowClick
        }
      ]

      if (this.isDesktopDevice) {
        items.push({
          key: 'hover',
          label: 'Hover(cnt)',
          max: Math.max(foldData.aboveHover, foldData.belowHover),
          aboveValue: foldData.aboveHover,
          belowValue: foldData.belowHover
        })
      }

      if (this.isDesktopDevice || this.reportPeriod.start < NEW_MOBILE_HEATMAP_STARTDATE) {
        items[1].label = 'Click(cnt)'
      }

      return items
    }
  },
  watch: {
    resizeTimestamp (value) {
      this.setImageWidth()
    },
    scrollPvRateChartContainerStyle (value) {
      this.setResizeTimestamp(new Date().getTime())
    },
    rawData () {
      this.$nextTick().then(() => {
        this.setResizeTimestamp(new Date().getTime())
      })
    }
  },
  mounted () {
    this.setImageWidth()
  },
  methods: {
    ...mapMutations([
      'setResizeTimestamp'
    ]),
    setImageWidth () {
      const el = this.$refs.imageContainer

      if (el) {
        this.chartImageWidth = el.offsetWidth

        if (this.isPhoneDevice) {
          this.chartImageWidth = Math.min(this.chartImageWidth, PHONE_IMAGE_WIDTH)
        }
      }
    },
    getChartOptions (item, isAbove) {
      let maxValue = Math.max(item.aboveValue, item.belowValue)
      let minInterval

      switch (item.key) {
        case 'height':
          minInterval = 500
          break
        default:
          if (maxValue <= 50) {
            minInterval = 10
          } else if (maxValue <= 100) {
            minInterval = 20
          } else if (maxValue <= 200) {
            minInterval = 50
          } else if (maxValue <= 300) {
            minInterval = 100
          } else if (maxValue <= 1000) {
            minInterval = 200
          } else if (maxValue <= 10000) {
            minInterval = 2000
          } else if (maxValue <= 100000) {
            minInterval = 20000
          } else if (maxValue <= 1000000) {
            minInterval = 200000
          } else if (maxValue <= 10000000) {
            minInterval = 2000000
          }
      }

      maxValue = maxValue - (maxValue % minInterval) + minInterval

      const xAxis = {
        type: 'value',
        boundaryGap: true,
        inverse: isAbove,
        max: maxValue,
        minInterval: minInterval,
        axisTick: {
          show: true
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#e6e6e6'
          }
        }
      }

      xAxis.axisLabel = {
        formatter: (value) => {
          if (value > 500) {
            value = Number((value / 1000).toFixed(1)) + 'K'
          }
          return value
        }
      }

      const options = {
        grid: {
          left: isAbove ? 20 : 40,
          right: isAbove ? 40 : 20
        },
        tooltip: {
          trigger: 'item'
        },
        xAxis: xAxis,
        yAxis: {
          type: 'category'
        },
        series: [
          {
            type: 'bar',
            data: [{
              name: item.label,
              value: isAbove ? item.aboveValue : item.belowValue
            }],
            barWidth: 10,
            itemStyle: {
              barBorderRadius: [5, 5, 5, 5]
            }
          }
        ],
        color: isAbove ? '#43b6c9' : '#ec0047'
      }
      return options
    },
    onExcelExport () {
      let vue = this
      let fileName = this.applyData.pageName + '_' + this.reportName
      let excel = new Excel()
      vue.$emit('onDownloading', true)
      excel.setReportInfo(this.applyData, this.infoData, this.reportData, this.referer, this.metric)
        .then(
          excel.setReportData(this.reportName, this.reportData, this.rawData, null, { highlight: 'F' })
            .then(result => {
              excel.download(fileName)
              vue.$emit('onDownloading', false)
            })
        )
    }
  }
}
</script>

<style lang="scss">
  .reporting__scroll {
    font-size: 13px;
    position: relative;

    h3 {
      font-size: 22px;
      font-weight: 500;
      color: #444444;
    }

    .reporting__export {
      position: absolute;
      top: -5px;
      right: 0;
      font-size: 12px;

      .icon {
        vertical-align: middle;
      }
    }

    & > .description {
      margin-top: 15px;
      line-height: 1.46;
      color: #555555;
    }

    .reporting__scroll__fold-title, .reporting__scroll__scrolled-heights-title{
      .title {
        font-size: 15px;
        color: #2f2f2f;
      }
      .description {
        margin-top: 13px;
        line-height: 1.46;
        color: #767676;
      }
    }

    .reporting__scroll__fold {
      margin-top: 20px;

      .reporting__scroll__fold-average {
        margin-top: 15px;
        padding: 15px 15px 17px;
        border-radius: 6px;
        border: solid 1px #d7d7d7;
        background-color: #f4f4f4;

        .value {
          font-size: 14px;
          color: $brandColor;
        }

        .description {
          margin-top: 10px;
          color: #2f2f2f;
        }
      }

      .reporting__scroll__fold-chart-legend {
        margin-top: 20px;

        ul {
          display: flex;
          justify-content: space-between;

          .legend {
            color: #2f2f2f;
          }

          li:nth-of-type(1) {
            .legend {
              margin-right: 5px;
            }
          }

          li:nth-of-type(2) {
            .legend {
              margin-left: 5px;
            }
          }
        }

        .chart-name {
          text-align: center;
          font-size: 14px;
          font-weight: 500;
          color: #2f2f2f;
          margin-top: 20px;
        }
      }

      .reporting__scroll__fold-chart {
        margin-top: 10px;
      }
    }

    .reporting__scroll__scrolled-heights {
      margin-top: 30px;

      .reporting__scroll__scrolled-heights-title {
        padding-bottom: 15px;
        border-bottom: 1px solid #e6e6e6;
      }

      .reporting__scroll__scrolled-heights-terms {
        margin-top: 20px;

        .terms {
          position: relative;
          padding-left: 21px;

          i {
            position: absolute;
            top: 1px;
            left: 0;
          }

          span {
            font-size: 14px;
            color: #444444;
          }

          p {
            margin-top: 9px;
            line-height: 1.46;
            color: #666666;
          }

          &:nth-of-type(1) {
            i {
              display: inline-block;
              width: 12px;
              height: 12px;
              border-radius: 2px;
              background-color: $subColor;
            }
          }
          &:nth-of-type(2) {
            margin-top: 20px;
          }
        }
      }

      .reporting__scroll__scolled-heights-chart {
        margin-top: 20px;
      }
    }

    .chart_wrap {
      position: relative;
      .left,
      .right {
        float: left;
        width: 50%;
        position: relative;
        z-index: 10;
      }
      .label {
        position: absolute;
        width: 100%;
        top: 38px;
        text-align: center;
        color: #666;
      }
    }
    .scroll_pv_rate_chart_container {
      position: relative;
    }
    .scroll_pv_rate_chart_image {
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      background-repeat: no-repeat;
      background-size: 100% 100%;
    }
    .scroll_avg_fold {
      pointer-events: none;
      position: absolute;
      left: 0;
      right: 0;
      height: 1px;
      background-color: #ec0047;
    }
  }
</style>
